import React, { useState, useCallback, useEffect } from 'react';

import { Col, Input, Layout, Pagination, Popconfirm, Row, Spin } from 'antd';
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cloneDeep } from 'lodash';

import PdButton from 'components/pd-button';
import SearchSuggestionService from 'services/search-suggestions';
import { errHandling } from 'utils';

import { reorder } from './utils';

import './styles.scss';

const { Content } = Layout;
const { Search } = Input;

export function SearchSuggestion() {
  const [cursor, setCursor] = useState(1);
  const [keywordList, setKeywordList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [querySearch, setQuerySearch] = useState('');
  const [totalKeywords, setTotalKeywords] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  const getKeywordList = useCallback(
    async (limit) => {
      setLoading(true);
      try {
        const {
          data: { data = [], metadata = {} },
        } = await SearchSuggestionService.getKeywordList({
          q: querySearch,
          limit,
          cursor,
        });

        setTotalKeywords(metadata.total_items);
        const parsedData = data.reduce((r, e) => {
          const [group] = e.keyword;
          if (!r[group]) {
            r[group] = { group, children: [e] };
          } else {
            r[group].children.push(e);
          }
          return r;
        }, {});

        const result = Object.values(parsedData);
        setKeywordList(result);
      } catch (error) {
        errHandling(error);
      } finally {
        setLoading(false);
      }
    },
    [querySearch, cursor]
  );

  const deleteKeyword = async (id) => {
    setLoading(true);
    try {
      await SearchSuggestionService.deleteKeyword(id);
      getKeywordList();
    } catch (error) {
      setLoading(false);
      errHandling(error);
    }
  };

  const updateKeywordOrder = async (payload) => {
    setLoading(true);
    try {
      await SearchSuggestionService.updateKeywordOrder({
        search_suggestions: payload,
      });
      getKeywordList();
    } catch (error) {
      setLoading(false);
      errHandling(error);
    }
  };

  useEffect(() => {
    getKeywordList(pageSize);
  }, [getKeywordList, pageSize]);

  function SortableItem({ value, index }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
      id: value.id || index,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      userSelect: 'none',
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        className="pd-cms-category--list draggable-item pd-margin-top-sm">
        <Row type="flex" align="middle" gutter={30}>
          <Col span={20} {...listeners}>
            <div style={{ cursor: 'grab', display: 'inline-block', marginRight: '8px' }}>
              ⋮⋮
            </div>
            <span>{value.keyword}</span>
          </Col>
          <Col span={4}>
            <Popconfirm
              cancelText="No"
              okText="Yes"
              onConfirm={() => deleteKeyword(value.id)}
              title="Are you sure delete this keyword?">
              <PdButton
                type="danger"
                full
                onClick={(e) => e.stopPropagation()}>
                Delete
              </PdButton>
            </Popconfirm>
          </Col>
        </Row>
      </div>
    );
  }

  function SortableList({ items, onSortEnd }) {
    const handleDragEnd = (event) => {
      const { active, over } = event;
      if (active && over && active.id !== over.id) {
        const oldIndex = items.findIndex((item, index) => (item.id || index) === active.id);
        const newIndex = items.findIndex((item, index) => (item.id || index) === over.id);
        onSortEnd({ oldIndex, newIndex });
      }
    };

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((item, index) => ({ id: item.id || index }))} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => {
            return <SortableItem key={item.id} index={index} value={item} />;
          })}
        </SortableContext>
      </DndContext>
    );
  }

  const onSortEnd = (group) => {
    return async ({ oldIndex, newIndex }) => {
      try {
        if (oldIndex === newIndex) return;
        const foundGroup = keywordList.find((item) => item.group === group);
        const reordered = reorder(
          cloneDeep(foundGroup.children),
          oldIndex,
          newIndex
        ).map((item, index) => {
          item.order_level = foundGroup.children[index].order_level;
          return item;
        });
        updateKeywordOrder(reordered);
      } catch (error) {
        errHandling(error);
      }
    };
  };

  return (
    <Layout className="main-container">
      <div className="content search-suggestions">
        <div className="pageTitle">Search Suggestion Section</div>
        <Content className="pageContent page-content-media">
          <Row gutter={16} justify="space-between">
            <Col span={8} className="pd-margin-bottom-md">
              <Search
                allowClear
                onSearch={(value) => setQuerySearch(value)}
                placeholder="Search"
                size="large"
              />
            </Col>
          </Row>
          <Row className="pd-sortable-list">
            <div className="pd-sortable-list-header">Product</div>
            <Spin spinning={loading}>
              {!loading && (
                <>
                  {keywordList.map((item) => (
                    <React.Fragment key={item.group}>
                      <div className="group">{item.group}</div>
                      <div id={`drag-content__${item.group}`}>
                        <SortableList
                          items={item.children}
                          onSortEnd={onSortEnd(item.group)}
                        />
                      </div>
                    </React.Fragment>
                  ))}

                  <div className="pagination-container">
                    <Pagination
                      current={cursor}
                      defaultCurrent={1}
                      onChange={(page) => setCursor(page)}
                      onShowSizeChange={(page, size) => {
                        setCursor(1);
                        setPageSize(size);
                      }}
                      pageSize={pageSize}
                      showSizeChanger
                      total={totalKeywords}
                    />
                  </div>
                </>
              )}
            </Spin>
          </Row>
        </Content>
      </div>
    </Layout>
  );
}
